// src/pages/client/ClientMessages.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  FiMessageSquare,
  FiSend,
  FiPaperclip,
  FiUser,
  FiSearch,
  FiClock,
} from "react-icons/fi";
import { useClient } from "../context/ClientContext";
import axios from "axios";

function ClientMessages() {
  const { client } = useClient();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      console.log("Fetching all messages for client");
      const token = localStorage.getItem("agritech-client-token");
      const response = await axios.get(
        "https://agri-back-mauve.vercel.app/api/client-portal/messages",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        console.log("Fetched messages:", response.data.data);
        setMessages(response.data.data);

        // Mark messages as read
        const unreadMessages = response.data.data.filter(
          (msg) => !msg.isRead && msg.recipientType === "client"
        );

        if (unreadMessages.length > 0) {
          await markMessagesAsRead(unreadMessages.map((msg) => msg._id));
        }
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async (messageIds) => {
    try {
      const token = localStorage.getItem("agritech-client-token");
      await axios.put(
        "https://agri-back-mauve.vercel.app/api/client-portal/messages/read",
        { messageIds },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);

    // Create optimistic message
    const tempMessage = {
      _id: Date.now().toString(),
      content: newMessage,
      senderId: {
        _id: client?.id,
        name: "You",
        email: client?.email,
      },
      senderType: "client",
      recipientId: "admin-system",
      recipientType: "consultant",
      createdAt: new Date().toISOString(),
      isRead: false,
    };

    setMessages((prev) => [...prev, tempMessage]);
    setNewMessage("");

    try {
      const token = localStorage.getItem("agritech-client-token");
      const response = await axios.post(
        "https://agri-back-mauve.vercel.app/api/client-portal/messages",
        {
          content: newMessage,
          attachments: [],
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        // Replace temp message with actual message from server
        setMessages((prev) =>
          prev
            .filter((msg) => msg._id !== tempMessage._id)
            .concat(response.data.data)
        );
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // Keep the optimistic message
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            </div>
            <div className="text-sm text-gray-500">
              Chat with Afrikonsults Admin
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex flex-col h-[600px]">
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <FiUser className="text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Afrikonsults Admin
                  </h3>
                  <p className="text-sm text-gray-500">Administrator</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
              {messages.length > 0 ? (
                <div className="space-y-4">
                  {messages.map((message, index) => {
                    const isClient = message.senderType === "client";
                    const showDate =
                      index === 0 ||
                      formatDate(messages[index - 1].createdAt) !==
                        formatDate(message.createdAt);

                    return (
                      <div key={message._id}>
                        {/* Date Separator */}
                        {showDate && (
                          <div className="flex justify-center my-4">
                            <span className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                              {formatDate(message.createdAt)}
                            </span>
                          </div>
                        )}

                        {/* Message */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${
                            isClient ? "justify-end" : "justify-start"
                          }`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md xl:max-w-lg ${
                              isClient
                                ? "bg-green-600 text-white rounded-l-2xl rounded-tr-2xl rounded-br-sm"
                                : "bg-white text-gray-900 rounded-r-2xl rounded-tl-2xl rounded-bl-sm border border-gray-200"
                            } p-4 shadow-sm`}
                          >
                            <div className="text-sm mb-1">
                              {message.content}
                            </div>
                            <div
                              className={`text-xs flex items-center space-x-1 ${
                                isClient ? "text-green-100" : "text-gray-500"
                              }`}
                            >
                              <FiClock size={10} />
                              <span>{formatTime(message.createdAt)}</span>
                              {isClient && (
                                <span>{message.isRead ? "✓✓" : "✓"}</span>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-gray-400 text-6xl mb-4">💬</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No messages yet
                    </h3>
                    <p className="text-gray-500">
                      Start a conversation with Afrikonsults Admin
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className="flex space-x-4">
                <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                  <FiPaperclip size={20} />
                </button>
                <div className="flex-1">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Type your message here..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  />
                </div>
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-end"
                >
                  <FiSend size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2">
              Need Immediate Help?
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              For urgent agricultural issues that need immediate attention.
            </p>
            <button className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm">
              Emergency Support
            </button>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2">
              Schedule a Call
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Prefer a voice conversation? Schedule a phone call.
            </p>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm">
              Book Call
            </button>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2">Share Files</h3>
            <p className="text-gray-600 text-sm mb-4">
              Upload photos or documents to help us understand your situation
              better.
            </p>
            <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm">
              Upload Files
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientMessages;
